/**
 * ReviewSidebar.tsx
 *
 * Slide-out inspection & approval panel.
 *
 * Opens when a `waiting_for_approval` DAG node is clicked.
 * The parent (`DAGCanvas`) is responsible for:
 *   - Supplying the live `node` prop (re-derived from dagMap on every SSE tick).
 *   - Detecting the status transition away from `waiting_for_approval` and
 *     calling `onClose` + surfacing a toast — the sidebar itself is stateless
 *     with respect to auto-close so it stays simple and testable.
 *
 * Approval API:
 *   POST /v1/runs/{runId}/approve
 *   { nodeId, decision: "approved"|"rejected", reason?: string }
 *
 * Layout:
 *   ┌──────────────────────────────┐
 *   │ Header: dept badge · title   │
 *   ├──────────────────────────────┤
 *   │ Proposed-action inspector    │
 *   │  • Lead table (if leads[])   │
 *   │  • Draft preview (per lead)  │
 *   │  • Fallback: JSON viewer     │
 *   ├──────────────────────────────┤
 *   │ Reject-reason textarea       │
 *   │ (visible only after Reject)  │
 *   ├──────────────────────────────┤
 *   │ [Approve & Execute] [Reject] │
 *   └──────────────────────────────┘
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Check, X } from 'lucide-react'
import { api, apiErrorMessage } from '../api/client'
import { useObservabilityStore } from '../store/useObservabilityStore'
import type { ApprovalRecord } from '../store/useObservabilityStore'
import type { GoDAGNode } from './DAGCanvas'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { ReviewHistoryList } from '../features/review/ReviewHistoryList'
import { asDraftReplyPayload, buildDraftReplyEditedPayload } from '../features/review/draftReply'
import type { DraftReplyPayload } from '../features/review/draftReply'
import { asExtractedInvoicePayload } from '../features/review/extractedInvoice'
import type { ExtractedInvoice } from '../features/review/extractedInvoice'

// ── Inline JSON viewer ────────────────────────────────────────────────────────
// Lightweight recursive component — no external syntax-highlight library needed.

function escapeStr(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
}

function JsonValue({
  value,
  depth = 0,
}: {
  value: unknown
  depth?: number
}) {
  const pad = { paddingLeft: 14 } as const

  if (value === null)             return <span className="text-[#64748b]">null</span>
  if (value === undefined)        return <span className="text-[#64748b]">undefined</span>
  if (typeof value === 'boolean') return <span className="text-[#c084fc]">{String(value)}</span>
  if (typeof value === 'number')  return <span className="text-[#fb923c]">{value}</span>
  if (typeof value === 'string') {
    return <span className="text-[#4ade80]">"{escapeStr(value)}"</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[#334155]">[]</span>
    return (
      <>
        <span className="text-[#334155]">{'['}</span>
        <div style={pad}>
          {value.map((item, i) => (
            <div key={i}>
              <JsonValue value={item} depth={depth + 1} />
              {i < value.length - 1 && <span className="text-[#334155]">,</span>}
            </div>
          ))}
        </div>
        <span className="text-[#334155]">{']'}</span>
      </>
    )
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) return <span className="text-[#334155]">{'{}'}</span>
    return (
      <>
        <span className="text-[#334155]">{'{'}</span>
        <div style={pad}>
          {entries.map(([k, v], i) => (
            <div key={k}>
              <span className="text-[#60a5fa]">"{k}"</span>
              <span className="text-[#334155]">: </span>
              <JsonValue value={v} depth={depth + 1} />
              {i < entries.length - 1 && <span className="text-[#334155]">,</span>}
            </div>
          ))}
        </div>
        <span className="text-[#334155]">{'}'}</span>
      </>
    )
  }

  return <>{String(value)}</>
}

// ── Lead Gen payload types ────────────────────────────────────────────────────

interface RawLead {
  company_name:      string
  industry:          string
  source_url:        string
  job_posting_title: string
  draft_outreach:    string
  relevance_score:   number
  signal_reason:     string
}

interface LeadGenPayload {
  leads:           RawLead[]
  target_industry: string
  scraped_at:      string
  source_url:      string
}

/** Returns true + a typed payload when proposedAction is a LeadGenPayload. */
function asLeadGenPayload(v: unknown): LeadGenPayload | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (!Array.isArray(o.leads) || o.leads.length === 0) return null
  return o as unknown as LeadGenPayload
}

// ── LeadTableView ─────────────────────────────────────────────────────────────
// Tabular summary of leads; clicking a row selects it for DraftPreview.

function ScorePip({ score }: { score: number }) {
  // Three-tier colour: ≥0.85 green, ≥0.70 amber, else muted.
  const color =
    score >= 0.85 ? '#4ade80' :
    score >= 0.70 ? '#fbbf24' :
    '#94a3b8'
  return (
    <span className="text-[11px] font-[800] tabular-nums" style={{ color }}>
      {(score * 100).toFixed(0)}%
    </span>
  )
}

function LeadTableView({
  payload,
  selectedIdx,
  onSelect,
}: {
  payload:     LeadGenPayload
  selectedIdx: number
  onSelect:    (idx: number) => void
}) {
  return (
    <div className="flex flex-col gap-[10px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-[800] tracking-[0.12em] uppercase text-[#fb923c]">
          {payload.target_industry}
        </span>
        <span className="text-[10px] font-semibold text-[#4e6480]">
          {payload.leads.length} lead{payload.leads.length !== 1 ? 's' : ''}
        </span>
      </div>

      <table
        className="w-full border-collapse text-[11.5px] border border-[rgba(148,163,184,0.1)] rounded-[10px] overflow-hidden"
        role="grid"
      >
        <thead>
          <tr className="bg-[rgba(6,10,16,0.6)]">
            <th className="px-[10px] py-[7px] text-left text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] border-b border-[rgba(148,163,184,0.1)]">Company</th>
            <th className="px-[10px] py-[7px] text-left text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] border-b border-[rgba(148,163,184,0.1)]">Role</th>
            <th className="px-[10px] py-[7px] text-right w-[52px] text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] border-b border-[rgba(148,163,184,0.1)]">Score</th>
          </tr>
        </thead>
        <tbody>
          {payload.leads.map((lead, i) => (
            <tr
              key={i}
              className={`cursor-pointer transition-colors duration-[120ms] outline-none ${i === selectedIdx ? 'bg-[rgba(56,189,248,0.07)] hover:bg-[rgba(56,189,248,0.11)]' : 'hover:bg-[rgba(148,163,184,0.05)]'}`}
              onClick={() => onSelect(i)}
              tabIndex={0}
              role="row"
              aria-selected={i === selectedIdx}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(i) }}
            >
              <td className="px-[10px] py-2 border-b border-[rgba(148,163,184,0.07)] align-top">
                <a
                  className="text-[#60a5fa] no-underline font-bold hover:underline"
                  href={lead.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title={lead.source_url}
                >
                  {lead.company_name}
                </a>
              </td>
              <td className="px-[10px] py-2 border-b border-[rgba(148,163,184,0.07)] align-top text-[#8fa3bd] text-[11px]">
                {lead.job_posting_title}
              </td>
              <td className="px-[10px] py-2 border-b border-[rgba(148,163,184,0.07)] align-top text-right">
                <ScorePip score={lead.relevance_score} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── DraftPreview ──────────────────────────────────────────────────────────────
// Renders the AI-drafted outreach email for the selected lead.

function DraftPreview({ lead }: { lead: RawLead }) {
  if (!lead.draft_outreach) return null
  return (
    <div className="mt-[14px] border-t border-[rgba(148,163,184,0.1)] pt-[14px] flex flex-col gap-2">
      <div className="flex items-baseline mb-0.5">
        <span className="block text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480]">
          Draft outreach
          <span className="ml-[6px] font-normal normal-case tracking-normal text-[#3a5068]">
            — {lead.company_name}
          </span>
        </span>
      </div>
      <div className="flex items-start gap-[6px] text-[11px] text-[#4e6480] leading-[1.5] px-[10px] py-[7px] bg-[rgba(251,146,60,0.06)] border border-[rgba(251,146,60,0.15)] rounded-lg">
        <span className="flex-shrink-0 mt-[1px]" aria-hidden="true">⚡</span>
        {lead.signal_reason}
      </div>
      <pre className="font-mono text-[11px] leading-[1.7] text-[#8fa3bd] bg-[rgba(6,10,16,0.7)] border border-[rgba(148,163,184,0.1)] rounded-[10px] px-[14px] py-3 m-0 whitespace-pre-wrap break-words overflow-x-auto [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.15)_transparent] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgba(148,163,184,0.15)] [&::-webkit-scrollbar-thumb]:rounded-sm">
        {lead.draft_outreach}
      </pre>
    </div>
  )
}

// ── Finance Invoice payload types ─────────────────────────────────────────────

interface InvoiceLineItem {
  description: string
  quantity:    number
  unit_price:  number
  tax_rate:    number
  total:       number
}

interface FinanceInvoicePayload {
  invoice_number: string
  client_name:    string
  client_email:   string
  issue_date:     string
  due_date:       string
  currency:       string
  line_items:     InvoiceLineItem[]
  subtotal:       number
  tax_total:      number
  total:          number
  draft_email:    string
  spreadsheet_id?: string
  sheet_name?:     string
  fetched_at?:     string
}

/** Returns the typed payload if proposedAction looks like a FinanceInvoicePayload. */
function asFinanceInvoicePayload(v: unknown): FinanceInvoicePayload | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (
    typeof o.invoice_number !== 'string' ||
    !Array.isArray(o.line_items) ||
    o.line_items.length === 0
  ) return null
  return o as unknown as FinanceInvoicePayload
}

// ── Gate 4: draft-reply (identity-request) approval editing ──────────────────
// The draft-reply payload type, guard, toHTML mirror, and editedPayload builder
// live in the shared module features/review/draftReply.ts (also consumed by the
// dashboard ApprovalDrawer). This file keeps only the presentation
// (DraftReplyPreview) and the submit wiring.

// ── Extracted Invoice payload (invoice-extractor / inbound AP bill) ────────────
// The extracted-invoice types and payload guard live in the shared module
// features/review/extractedInvoice.ts (also consumed by the dashboard Finance
// Approvals page). This file keeps only the presentation
// (ExtractedInvoicePreview).

// ── FinancePreview ─────────────────────────────────────────────────────────────

function fmtMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style:    'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

function FinancePreview({
  payload,
  draftEmail,
  onDraftEmailChange,
}: {
  payload:            FinanceInvoicePayload
  draftEmail:         string
  onDraftEmailChange: (v: string) => void
}) {
  const cur = payload.currency || 'GBP'

  return (
    <div className="flex flex-col gap-0">
      {/* ── Invoice header ── */}
      <div className="flex justify-between items-start gap-3 pb-[14px] border-b border-[rgba(148,163,184,0.08)] flex-wrap">
        <div className="flex flex-col gap-[6px] min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-[800] tracking-[0.14em] uppercase text-[#3a5068]">Invoice</span>
            <code className="font-mono text-[12.5px] text-[#34d399] font-bold">{payload.invoice_number}</code>
          </div>
          <div className="flex flex-col gap-0.5 text-[12px] text-[#c8d9ee]">
            <span>{payload.client_name}</span>
            {payload.client_email && (
              <a
                href={`mailto:${payload.client_email}`}
                className="text-[11px] text-[#4e6480] no-underline transition-colors duration-[130ms] hover:text-[#38bdf8]"
                onClick={(e) => e.stopPropagation()}
              >
                {payload.client_email}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end flex-shrink-0">
          {payload.issue_date && (
            <div className="flex gap-[5px] items-center text-[11px]">
              <span className="text-[#3a5068] font-bold text-[10px] tracking-[0.08em] uppercase">Issued</span>
              <span className="text-[#8fa3bd] tabular-nums">{payload.issue_date}</span>
            </div>
          )}
          {payload.due_date && (
            <div className="flex gap-[5px] items-center text-[11px]">
              <span className="text-[#3a5068] font-bold text-[10px] tracking-[0.08em] uppercase">Due</span>
              <span className="text-[#fbbf24] font-bold tabular-nums">{payload.due_date}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Line items table ── */}
      <div className="overflow-x-auto mt-[14px] [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.1)_transparent]">
        <table className="w-full border-collapse text-[11.5px]" role="grid">
          <thead>
            <tr>
              <th className="text-left text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Description</th>
              <th className="text-right text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Qty</th>
              <th className="text-right text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Unit</th>
              <th className="text-right text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Tax</th>
              <th className="text-right text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Total</th>
            </tr>
          </thead>
          <tbody>
            {payload.line_items.map((item, i) => (
              <tr key={i} className="border-b border-[rgba(148,163,184,0.05)] transition-colors duration-100 hover:bg-[rgba(148,163,184,0.04)]">
                <td className="py-[6px] pr-[10px] text-[#c8d9ee] align-top">{item.description}</td>
                <td className="py-[6px] text-right text-[#8fa3bd] align-top">{item.quantity}</td>
                <td className="py-[6px] text-right text-[#8fa3bd] align-top">{fmtMoney(item.unit_price, cur)}</td>
                <td className="py-[6px] text-right text-[#4e6480] align-top">
                  {item.tax_rate > 0 ? `${(item.tax_rate * 100).toFixed(0)}%` : '—'}
                </td>
                <td className="py-[6px] text-right text-[#c8d9ee] tabular-nums align-top">
                  {fmtMoney(item.total, cur)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-bold">
              <td className="py-1 pt-[10px] border-t border-[rgba(148,163,184,0.1)] text-[11px] text-[#4e6480]" colSpan={4}>Subtotal</td>
              <td className="py-1 pt-[10px] border-t border-[rgba(148,163,184,0.1)] text-right text-[11px] text-[#4e6480]">{fmtMoney(payload.subtotal, cur)}</td>
            </tr>
            <tr>
              <td className="py-1 text-[11px] text-[#4e6480]" colSpan={4}>VAT / Tax</td>
              <td className="py-1 text-right text-[11px] text-[#4e6480]">{fmtMoney(payload.tax_total, cur)}</td>
            </tr>
            <tr>
              <td className="py-1 pt-2 border-t border-[rgba(52,211,153,0.2)] text-[13px] font-[800] text-[#34d399]" colSpan={4}>Total Due</td>
              <td className="py-1 pt-2 border-t border-[rgba(52,211,153,0.2)] text-right text-[13px] font-[800] text-[#34d399]">{fmtMoney(payload.total, cur)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── Draft email (editable) ── */}
      {draftEmail !== undefined && (
        <div className="mt-4">
          <div className="mb-2 text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480]">
            Draft email
            <span className="ml-[6px] font-normal normal-case tracking-normal text-[#3a5068]">— edit before approving</span>
          </div>
          <textarea
            className="w-full bg-[rgba(6,10,16,0.6)] border border-[rgba(148,163,184,0.12)] rounded-lg text-[#8fa3bd] font-mono text-[10.5px] leading-[1.65] px-3 py-[10px] resize-y outline-none transition-colors duration-[140ms] min-h-[160px] box-border focus:border-[rgba(52,211,153,0.3)] focus:text-[#c8d9ee]"
            value={draftEmail}
            rows={12}
            spellCheck={false}
            onChange={(e) => onDraftEmailChange(e.target.value)}
            aria-label="Draft invoice email"
          />
        </div>
      )}

      {/* ── Sheet reference ── */}
      {payload.spreadsheet_id && (
        <div className="inline-flex items-center gap-[6px] mt-3 bg-[rgba(52,211,153,0.06)] border border-[rgba(52,211,153,0.14)] rounded-[6px] px-2 py-0.5">
          <span className="text-[9px] font-[800] tracking-[0.1em] uppercase text-[#3a5068]">Source</span>
          <code className="font-mono text-[10px] text-[#34d399]">
            {payload.sheet_name ?? 'Sheet1'} · {payload.spreadsheet_id.slice(0, 16)}…
          </code>
        </div>
      )}
    </div>
  )
}

// ── ExtractedInvoicePreview ───────────────────────────────────────────────────
// Read-only summary for an inbound vendor bill from invoice-extractor.
// No editable draft email — approval commits the bill to Xero, it sends nothing.

function ExtractedInvoicePreview({ invoice }: { invoice: ExtractedInvoice }) {
  const cur = invoice.currency || 'GBP'
  return (
    <div className="flex flex-col gap-0">
      {/* ── Header: vendor + invoice no + total + due ── */}
      <div className="flex justify-between items-start gap-3 pb-[14px] border-b border-[rgba(148,163,184,0.08)] flex-wrap">
        <div className="flex flex-col gap-[6px] min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-[800] tracking-[0.14em] uppercase text-[#3a5068]">Invoice</span>
            <code className="font-mono text-[12.5px] text-[#34d399] font-bold">{invoice.invoiceNumber}</code>
          </div>
          <div className="flex flex-col gap-0.5 text-[12px] text-[#c8d9ee]">
            <span>{invoice.vendorName}</span>
            {invoice.vendorEmail && (
              <a
                href={`mailto:${invoice.vendorEmail}`}
                className="text-[11px] text-[#4e6480] no-underline transition-colors duration-[130ms] hover:text-[#38bdf8]"
                onClick={(e) => e.stopPropagation()}
              >
                {invoice.vendorEmail}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end flex-shrink-0">
          <div className="flex gap-[5px] items-baseline">
            <span className="text-[#3a5068] font-bold text-[10px] tracking-[0.08em] uppercase">Total</span>
            <span className="text-[#34d399] font-[800] text-[13px] tabular-nums">{fmtMoney(invoice.amount, cur)}</span>
          </div>
          {invoice.dueDate && (
            <div className="flex gap-[5px] items-center text-[11px]">
              <span className="text-[#3a5068] font-bold text-[10px] tracking-[0.08em] uppercase">Due</span>
              <span className="text-[#fbbf24] font-bold tabular-nums">{invoice.dueDate}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── AI summary ── */}
      {invoice.aiSummary && (
        <div className="mt-[14px] flex items-start gap-[6px] text-[11px] text-[#8fa3bd] leading-[1.55] px-[10px] py-[8px] bg-[rgba(52,211,153,0.05)] border border-[rgba(52,211,153,0.14)] rounded-lg">
          <span className="flex-shrink-0 mt-[1px]" aria-hidden="true">✦</span>
          {invoice.aiSummary}
        </div>
      )}

      {/* ── Line items ── */}
      <div className="overflow-x-auto mt-[14px] [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.1)_transparent]">
        <table className="w-full border-collapse text-[11.5px]" role="grid">
          <thead>
            <tr>
              <th className="text-left text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Description</th>
              <th className="text-right text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Qty</th>
              <th className="text-right text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] pb-[6px] border-b border-[rgba(148,163,184,0.1)]">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.map((item, i) => (
              <tr key={i} className="border-b border-[rgba(148,163,184,0.05)] transition-colors duration-100 hover:bg-[rgba(148,163,184,0.04)]">
                <td className="py-[6px] pr-[10px] text-[#c8d9ee] align-top">{item.description}</td>
                <td className="py-[6px] text-right text-[#8fa3bd] tabular-nums align-top">{item.quantity}</td>
                <td className="py-[6px] text-right text-[#c8d9ee] tabular-nums align-top">{fmtMoney(item.amount, cur)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="py-1 pt-2 border-t border-[rgba(52,211,153,0.2)] text-[13px] font-[800] text-[#34d399]" colSpan={2}>Total</td>
              <td className="py-1 pt-2 border-t border-[rgba(52,211,153,0.2)] text-right text-[13px] font-[800] text-[#34d399]">{fmtMoney(invoice.amount, cur)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

// ── Department accent map (kept in sync with DAGCanvas) ───────────────────────

const DEPT_ACCENT: Record<string, string> = {
  Research:    '#38bdf8',
  HR:          '#60a5fa',
  Finance:     '#34d399',
  Recruitment: '#a78bfa',
  Legal:       '#f472b6',
  Sales:       '#fb923c',
  Operations:  '#94a3b8',
  Engineering: '#f87171',
  Product:     '#fbbf24',
}
const DEPT_DEFAULT = '#64748b'

// ── Approval payload ──────────────────────────────────────────────────────────

type ApprovePayload = {
  nodeId:            string
  decision:          'approved' | 'rejected'
  reason?:           string
  editedDraftEmail?: string // finance invoice: human-edited email body
}

// ── Component ─────────────────────────────────────────────────────────────────

export type ReviewSidebarProps = {
  /** The live node object re-derived from dagMap on every SSE tick. null = closed. */
  node:    GoDAGNode | null
  runId:   string
  onClose: () => void
  onToast: (message: string, kind: 'success' | 'error') => void
}

// ── Decision banner (read-only mode) ──────────────────────────────────────────

function formatDecisionDate(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch {
    return iso
  }
}

function DecisionBanner({ approval }: { approval: ApprovalRecord | null }) {
  if (!approval) return null
  const isApproved = approval.status === 'approved'
  const color      = isApproved ? '#4ade80' : '#f87171'
  const icon       = isApproved ? '✓' : '✗'
  const label      = isApproved ? 'Approved' : 'Rejected'
  return (
    <div
      className="mx-5 mb-4 px-[14px] py-3 border rounded-[0px] flex items-start gap-[10px] flex-shrink-0"
      style={{ borderColor: `${color}30`, background: `${color}0c` }}
    >
      <span className="text-[18px] leading-none flex-shrink-0 mt-[1px]" style={{ color }}>{icon}</span>
      <div className="flex flex-col gap-[3px]">
        <span className="text-[13px] font-[800]" style={{ color }}>{label}</span>
        {approval.decidedAt && (
          <span className="text-[11.5px] text-[#8fa3bd]">
            on {formatDecisionDate(approval.decidedAt)}
          </span>
        )}
        {approval.decidedByUserID && (
          <span className="text-[11px] text-[#4e6480]">
            by <code className="font-mono text-[10.5px] text-[#64748b]">{approval.decidedByUserID}</code>
          </span>
        )}
      </div>
    </div>
  )
}

// Safety-net timeout (ms): force the approve/reject spinner to clear if the
// request never settles, so a stalled POST can't hang the button indefinitely.
const APPROVE_SPINNER_TIMEOUT_MS = 10000

// DraftReplyPreview renders an editable identity-request reply. Recipient (to) is
// READ-ONLY by design — editing wording is safe; redirecting the recipient of an
// identity request is a governance hazard and is not permitted from this surface.
function DraftReplyPreview(props: {
  payload:         DraftReplyPayload
  subjectValue:    string
  textValue:       string
  onSubjectChange: (v: string) => void
  onTextChange:    (v: string) => void
  disabled:        boolean
}) {
  const { payload, subjectValue, textValue, onSubjectChange, onTextChange, disabled } = props
  const subjChanged = subjectValue !== payload.subject
  const textChanged = textValue !== payload.text
  return (
    <div className="flex flex-col gap-[14px]">
      <div>
        <label className="block text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480] mb-2">
          Recipient
          <span className="ml-[6px] font-normal normal-case tracking-normal text-[#3a5068]">read-only</span>
        </label>
        <div className="text-[12px] text-[#8fa3bd] bg-[rgba(6,10,16,0.5)] border border-dashed border-[rgba(148,163,184,0.18)] rounded-lg px-3 py-[9px] break-words">
          {payload.to.join(', ')}
        </div>
      </div>
      <div>
        <label className="block text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480] mb-2" htmlFor="drp-subject">
          Subject
          {subjChanged && (
            <span className="ml-[6px] font-[800] normal-case tracking-normal text-[#fbbf24]">edited</span>
          )}
        </label>
        <input
          id="drp-subject"
          className="w-full box-border bg-[rgba(6,10,16,0.6)] border border-[rgba(148,163,184,0.12)] rounded-lg text-[#c8d9ee] font-[inherit] text-[12px] leading-[1.55] px-3 py-[9px] outline-none transition-colors duration-[140ms] focus:border-[rgba(52,211,153,0.3)] disabled:opacity-[0.55]"
          value={subjectValue}
          disabled={disabled}
          onChange={(e) => onSubjectChange(e.target.value)}
        />
      </div>
      <div>
        <label className="block text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480] mb-2" htmlFor="drp-message">
          Message
          {textChanged && (
            <span className="ml-[6px] font-[800] normal-case tracking-normal text-[#fbbf24]">edited</span>
          )}
        </label>
        <textarea
          id="drp-message"
          className="w-full bg-[rgba(6,10,16,0.6)] border border-[rgba(148,163,184,0.12)] rounded-lg text-[#8fa3bd] font-mono text-[10.5px] leading-[1.65] px-3 py-[10px] resize-y outline-none transition-colors duration-[140ms] min-h-[160px] box-border focus:border-[rgba(52,211,153,0.3)] focus:text-[#c8d9ee] disabled:opacity-[0.55]"
          value={textValue}
          disabled={disabled}
          rows={8}
          spellCheck={false}
          onChange={(e) => onTextChange(e.target.value)}
        />
      </div>
      <p className="m-0 text-[11px] text-[#4e6480] leading-[1.5]">
        On approve, this reply is sent immediately to the recipient. Your edits are applied
        before sending.
      </p>
    </div>
  )
}

export function ReviewSidebar({ node, runId, onClose, onToast }: ReviewSidebarProps) {
  // ── Store state ──────────────────────────────────────────────────────────────
  const isReadOnly = useObservabilityStore((s) => s.isReadOnly)

  // Mobile responsive: switch to bottom sheet on narrow viewports.
  const isMobile = useMediaQuery('(max-width: 767px)')

  // Gate 4 fix: the approval id now rides on the DAG node (backend emits node.approvalId),
  // so we read it directly instead of matching against the store's approvals map — which the
  // DAG view never hydrates. See fix/dag-node-approval-id.
  const nodeApprovalId: string | null =
    (node?.approvalId && typeof node.approvalId === 'string') ? node.approvalId : null

  // ── Local state ─────────────────────────────────────────────────────────────
  const [submitting,      setSubmitting]      = useState<'approved' | 'rejected' | null>(null)
  // Sticky done-state set after a 2xx decision: keeps the buttons disabled with a
  // done label and no spinner, even if the SSE never fires to auto-close.
  const [submitted,       setSubmitted]       = useState<'approved' | 'rejected' | null>(null)
  const [rejectOpen,      setRejectOpen]      = useState(false)
  const [rejectReason,    setRejectReason]    = useState('')
  const [selectedLeadIdx, setSelectedLeadIdx] = useState(0)
  // Finance preview: track draft email edits so the edited text is submitted.
  const [financeDraft, setFinanceDraft] = useState('')
  // Gate 4: draft-reply edit state (subject + text; html is regenerated on submit).
  const [replySubject, setReplySubject] = useState('')
  const [replyText, setReplyText]       = useState('')

  // Stable ref to the runId so the async handler always uses the current value.
  const runIdRef = useRef(runId)
  useEffect(() => { runIdRef.current = runId }, [runId])

  // Pending safety-timeout id for the approve/reject spinner (see handleDecision).
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Clear the safety-timeout on unmount so it can never fire after teardown.
  useEffect(() => () => {
    if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current)
  }, [])

  // Reset local state whenever the active node changes (user clicked a different
  // amber node while the sidebar was already open).
  const prevNodeId = useRef<string | null>(null)
  useEffect(() => {
    if (node?.id !== prevNodeId.current) {
      prevNodeId.current = node?.id ?? null
      setSubmitting(null)
      setSubmitted(null)
      // Cancel any in-flight spinner safety-timeout from the previous node.
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current)
        submitTimeoutRef.current = null
      }
      setRejectOpen(false)
      setRejectReason('')
      setSelectedLeadIdx(0)
      // Seed the draft email textarea from the payload when opening a finance node.
      const fp = asFinanceInvoicePayload(node?.proposedAction)
      setFinanceDraft(fp?.draft_email ?? '')
      // Seed the draft-reply editor from the payload when opening a draft-reply node.
      const drp = asDraftReplyPayload(node?.proposedAction)
      setReplySubject(drp?.subject ?? '')
      setReplyText(drp?.text ?? '')
    }
  }, [node?.id])

  // ── Approval handler ────────────────────────────────────────────────────────

  async function handleDecision(decision: 'approved' | 'rejected') {
    if (!node || submitting || submitted) return
    setSubmitting(decision)

    // Arm the safety-net timeout: if the request never settles (stalled POST,
    // dropped connection) force the spinner to clear so it can't hang forever.
    if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current)
    submitTimeoutRef.current = setTimeout(() => {
      submitTimeoutRef.current = null
      setSubmitting(null)
    }, APPROVE_SPINNER_TIMEOUT_MS)

    const payload: ApprovePayload = {
      nodeId: node.id,
      decision,
      ...(decision === 'rejected' && rejectReason.trim()
        ? { reason: rejectReason.trim() }
        : {}),
      // For finance invoice nodes, include the (possibly edited) draft email so
      // the backend can use the human-reviewed version when dispatching the invoice.
      ...(financePayload && decision === 'approved' && financeDraft !== financePayload.draft_email
        ? { editedDraftEmail: financeDraft }
        : {}),
    }

    try {
      if (draftReplyPayload) {
        // Gate 4: draft-reply approvals go to the approval-keyed /decision endpoint,
        // which carries editedPayload (the /runs/{id}/approve endpoint drops edits).
        if (!nodeApprovalId) {
          onToast('Cannot submit: approval is still loading — try again in a moment.', 'error')
          setSubmitting(null)
          return
        }
        const editedPayload = buildDraftReplyEditedPayload(
          draftReplyPayload,
          { subject: replySubject, text: replyText },
        )
        await api.post<unknown>(
          `/v1/approvals/${encodeURIComponent(nodeApprovalId)}/decision`,
          {
            status: decision,
            ...(decision === 'approved' && Object.keys(editedPayload).length > 0
              ? { editedPayload }
              : {}),
          },
        )
      } else {
        await api.post<unknown>(
          `/v1/runs/${encodeURIComponent(runIdRef.current)}/approve`,
          payload,
        )
      }
      // Success (HTTP 2xx) — record the sticky done-state so the buttons settle
      // into a disabled "Approved"/"Rejected" label with no spinner. The parent
      // still auto-closes the sidebar when the SSE confirms the node
      // transitioned, but we no longer depend on that stream (the spinner is
      // cleared in `finally` regardless).
      setSubmitted(decision)
    } catch (err) {
      // API call failed — surface the error (buttons re-enabled in `finally`).
      onToast(
        apiErrorMessage(err, 'Approval request failed'),
        'error',
      )
    } finally {
      // The request settled (success or failure): stop the spinner now and
      // cancel the safety-timeout. This is what makes a 200 clear the spinner
      // immediately, independent of the SSE stream.
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current)
        submitTimeoutRef.current = null
      }
      setSubmitting(null)
    }
  }

  // ── Keyboard shortcut: Escape closes ────────────────────────────────────────

  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape' && node) onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [node, onClose])

  // ── Derived display values ───────────────────────────────────────────────────

  // Buttons lock while a decision is in flight (submitting) or already recorded
  // (submitted — the sticky 2xx done-state).
  const actionsDisabled = submitting !== null || submitted !== null
  const isOpen          = node !== null
  const deptColor       = DEPT_ACCENT[node?.department ?? ''] ?? DEPT_DEFAULT
  const hasPayload      = node?.proposedAction != null && Object.keys(node.proposedAction).length > 0
  const leadPayload     = asLeadGenPayload(node?.proposedAction)
  const financePayload  = !leadPayload ? asFinanceInvoicePayload(node?.proposedAction) : null
  const extractedInvoice = !leadPayload && !financePayload
    ? asExtractedInvoicePayload(node?.proposedAction)
    : null
  const draftReplyPayload = !leadPayload && !financePayload && !extractedInvoice
    ? asDraftReplyPayload(node?.proposedAction)
    : null

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop — pointer-events:none so canvas interactions still work */}
      <div
        className={`absolute inset-0 bg-[rgba(4,8,14,0.42)] pointer-events-none transition-opacity duration-[260ms] z-[9] ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        className={[
          'absolute top-0 right-0 w-[360px] max-w-full h-full z-10',
          'flex flex-col overflow-hidden',
          'bg-[rgba(10,16,24,0.97)] border-l border-[rgba(148,163,184,0.13)] backdrop-blur-[12px]',
          'transition-transform duration-[260ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]',
          isOpen ? 'translate-x-0' : 'translate-x-full',
          isMobile ? 'fixed bottom-0 left-0 right-0 max-h-[80vh] rounded-t-2xl' : '',
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={node ? `Review: ${node.label}` : 'Review panel'}
      >
        {node && (
          <>
            {/* ── Header ── */}
            <div className="relative px-5 pt-[18px] pb-4 border-b border-[rgba(148,163,184,0.13)] flex-shrink-0">
              <div className="flex items-center gap-2 mb-[10px]">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-[800] tracking-[0.1em] uppercase border border-transparent whitespace-nowrap"
                  style={{
                    color:       deptColor,
                    background:  `${deptColor}16`,
                    borderColor: `${deptColor}30`,
                  }}
                >
                  {node.department || node.stepType}
                </span>
                <span className="text-[10px] font-semibold text-[#4e6480] tabular-nums">step #{node.stepIndex}</span>
              </div>

              <h2 className="text-[16px] font-[800] text-[#dce9f7] m-0 mb-[6px] leading-[1.3] pr-7 tracking-[-0.02em]">
                {node.label}
              </h2>

              <p className="text-[11px] text-[#4e6480] m-0 leading-[1.5]">
                {isReadOnly
                  ? 'Historical run — decision already recorded.'
                  : 'Awaiting your decision before execution continues.'}
              </p>

              <button
                className="absolute top-[14px] right-[14px] w-6 h-6 flex items-center justify-center border-none bg-transparent text-[#4e6480] cursor-pointer rounded-[6px] text-[12px] transition-colors duration-[140ms] leading-none hover:bg-[rgba(148,163,184,0.1)] hover:text-[#8fa3bd]"
                onClick={onClose}
                aria-label="Close review panel"
                title="Close (Esc)"
              >
                <X size={16} />
              </button>
            </div>

            {/* ── Proposed-action inspector ── */}
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 rsb-body-scroll">
              <span className="block text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480] mb-[10px]">
                Proposed action
              </span>

              {leadPayload ? (
                // ── Lead Gen payload: structured table + draft preview ──────────
                <>
                  <LeadTableView
                    payload={leadPayload}
                    selectedIdx={selectedLeadIdx}
                    onSelect={setSelectedLeadIdx}
                  />
                  <DraftPreview lead={leadPayload.leads[selectedLeadIdx]} />
                </>
              ) : financePayload ? (
                // ── Finance Invoice payload: line-items table + editable draft ──
                <FinancePreview
                  payload={financePayload}
                  draftEmail={financeDraft}
                  onDraftEmailChange={setFinanceDraft}
                />
              ) : extractedInvoice ? (
                // ── invoice-extractor result (nested __finance_invoice) ──────────
                <ExtractedInvoicePreview invoice={extractedInvoice} />
              ) : draftReplyPayload ? (
                // ── Gate 4: draft-reply (identity-request) editable reply ────────
                <DraftReplyPreview
                  payload={draftReplyPayload}
                  subjectValue={replySubject}
                  textValue={replyText}
                  onSubjectChange={setReplySubject}
                  onTextChange={setReplyText}
                  disabled={submitting !== null || submitted !== null}
                />
              ) : hasPayload ? (
                // ── Generic payload: syntax-highlighted JSON fallback ──────────
                <div
                  className="rsb-json-viewer font-mono text-[11.5px] leading-[1.65] text-[#8fa3bd] bg-[rgba(6,10,16,0.7)] border border-[rgba(148,163,184,0.1)] rounded-[10px] px-4 py-[14px] overflow-x-auto break-words"
                  role="region"
                  aria-label="Proposed action payload"
                >
                  <JsonValue value={node.proposedAction} />
                </div>
              ) : (
                <div className="text-[12px] text-[#3a5068] italic leading-[1.5] px-4 py-3 bg-[rgba(6,10,16,0.5)] border border-[rgba(148,163,184,0.08)] rounded-[10px]">
                  No structured payload — the agent is requesting permission to
                  proceed with its current context.
                </div>
              )}
            </div>

            {/* ── Decision banner (read-only: recorded decision) ── */}
            {isReadOnly && <DecisionBanner approval={null} />}

            {/* ── Rejection reason (shown only after Reject is clicked) ── */}
            {!isReadOnly && !draftReplyPayload && (
              <div
                className={`rsb-reject-area${rejectOpen ? ' rsb-reject-area--open' : ''}`}
                aria-hidden={!rejectOpen}
              >
                <label className="block text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480] mb-[10px]" htmlFor="rsb-reject-reason">
                  Reason for rejection
                  <span className="ml-[6px] font-normal normal-case tracking-normal text-[#3a5068]">
                    (forwarded to the LLM for re-planning)
                  </span>
                </label>
                <textarea
                  id="rsb-reject-reason"
                  className="w-full box-border bg-[rgba(6,10,16,0.7)] border border-[rgba(148,163,184,0.14)] rounded-lg text-[#c8d9ee] font-[inherit] text-[12px] leading-[1.55] px-3 py-[9px] resize-y outline-none transition-colors duration-[150ms] placeholder:text-[#3a5068] focus:border-[rgba(239,68,68,0.45)]"
                  placeholder="Describe what needs to change…"
                  value={rejectReason}
                  maxLength={2000}
                  rows={3}
                  onChange={(e) => setRejectReason(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                    // Ctrl+Enter / Cmd+Enter submits the rejection
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      void handleDecision('rejected')
                    }
                  }}
                  tabIndex={rejectOpen ? 0 : -1}
                />
              </div>
            )}

            {/* ── Action bar (hidden in read-only mode) ── */}
            {!isReadOnly && (
              <div className="flex gap-[10px] px-5 pt-[14px] pb-[18px] border-t border-[rgba(148,163,184,0.13)] flex-shrink-0">
                {/* Reject button */}
                {!rejectOpen ? (
                  <button
                    className="flex-1 inline-flex items-center justify-center gap-[7px] px-[14px] py-[10px] rounded-[10px] border border-[rgba(239,68,68,0.3)] bg-transparent text-[#f87171] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[140ms] min-h-[40px] whitespace-nowrap hover:not-disabled:bg-[rgba(239,68,68,0.08)] hover:not-disabled:border-[rgba(239,68,68,0.5)] disabled:opacity-[0.55] disabled:cursor-not-allowed active:not-disabled:scale-[0.97]"
                    disabled={actionsDisabled}
                    onClick={() => (draftReplyPayload ? void handleDecision('rejected') : setRejectOpen(true))}
                  >
                    {draftReplyPayload ? 'Reject' : 'Reject / Re-plan'}
                  </button>
                ) : (
                  <button
                    className={`flex-1 inline-flex items-center justify-center gap-[7px] px-[14px] py-[10px] rounded-[10px] border border-[rgba(239,68,68,0.4)] bg-[rgba(239,68,68,0.14)] text-[#fca5a5] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[140ms] min-h-[40px] whitespace-nowrap hover:not-disabled:bg-[rgba(239,68,68,0.22)] disabled:opacity-[0.55] disabled:cursor-not-allowed active:not-disabled:scale-[0.97]${submitting === 'rejected' ? ' opacity-80' : ''}`}
                    disabled={actionsDisabled}
                    onClick={() => void handleDecision('rejected')}
                  >
                    {submitting === 'rejected' ? (
                      <span className="rsb-spinner" aria-hidden="true" />
                    ) : null}
                    {submitting === 'rejected'
                      ? 'Sending…'
                      : submitted === 'rejected'
                        ? 'Rejected'
                        : 'Confirm Reject'}
                  </button>
                )}

                {/* Approve button */}
                <button
                  className={`flex-1 inline-flex items-center justify-center gap-[7px] px-[14px] py-[10px] rounded-[10px] border border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.16)] text-[#4ade80] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[140ms] min-h-[40px] whitespace-nowrap hover:not-disabled:bg-[rgba(34,197,94,0.26)] hover:not-disabled:border-[rgba(34,197,94,0.6)] disabled:opacity-[0.55] disabled:cursor-not-allowed active:not-disabled:scale-[0.97]${submitting === 'approved' ? ' opacity-80' : ''}`}
                  disabled={actionsDisabled}
                  onClick={() => void handleDecision('approved')}
                >
                  {submitting === 'approved' ? (
                    <span className="rsb-spinner" aria-hidden="true" />
                  ) : (
                    <span className="text-[13px] leading-none" aria-hidden="true"><Check size={14} /></span>
                  )}
                  {submitting === 'approved'
                    ? 'Waiting for confirmation…'
                    : submitted === 'approved'
                      ? 'Approved'
                      : 'Approve & Execute'}
                </button>
              </div>
            )}

            {/* ── Review history (collapsible) ── */}
            <details className="border-t border-[rgba(93,131,185,0.12)] mt-2 group flex-shrink-0 max-h-[40%] overflow-y-auto">
              <summary className="cursor-pointer select-none px-4 py-2.5 text-xs font-semibold text-text-muted hover:text-text-primary transition-colors">
                Review history
              </summary>
              <ReviewHistoryList runId={runId} refreshKey={submitting} />
            </details>
          </>
        )}
      </aside>
    </>
  )
}
